import { useCallback } from 'react';

import { Currency } from '__generated__/globalTypes';
import {
  MonetaryInput,
  Props as MonetaryInputProps,
} from 'atoms/inputs/MonetaryInput';
import { useCurrentUserContext } from 'contexts/currentUser';
import { MonetaryAmount } from 'lib/monetaryAmount';

import { Field, Props as FieldProps, RenderProps } from '../Field';

type Props = {
  name: string;
  defaultCurrency?: Currency;
  onChange?: (monetaryAmount: MonetaryAmount) => void;
  onToggleEthDisplay?: (ethDisplay: boolean) => void;
  placeholder?: string;
} & Omit<FieldProps<MonetaryAmount>, 'render'> &
  Omit<
    MonetaryInputProps,
    'fiatCurrency' | 'onChange' | 'onlyShowFiatCurrency' | 'cryptocurrency'
  >;

export const MonetaryInputField = ({
  name,
  defaultValue,
  onChange,
  validate,
  ...rest
}: Props) => {
  const {
    cryptocurrency,
    fiatCurrency: { code: currencyCode },
    walletPreferences: { onlyShowFiatCurrency },
  } = useCurrentUserContext();

  const render = useCallback(
    ({ error, handleChange, disabled }: RenderProps<MonetaryAmount>) => {
      return (
        <MonetaryInput
          defaultValue={defaultValue}
          fiatCurrency={currencyCode}
          cryptocurrency={cryptocurrency}
          onChange={(newMonetaryAmount, target) => {
            onChange?.(newMonetaryAmount);
            handleChange(newMonetaryAmount, target);
          }}
          onlyShowFiatCurrency={onlyShowFiatCurrency}
          error={error}
          {...rest}
          disabled={disabled || rest.disabled}
        />
      );
    },
    [
      defaultValue,
      currencyCode,
      cryptocurrency,
      onlyShowFiatCurrency,
      rest,
      onChange,
    ]
  );

  return (
    <Field
      defaultValue={defaultValue}
      name={name}
      validate={validate}
      render={render}
    />
  );
};
